import { matchPath } from 'react-router';
import { useCallback } from 'react';

import { deepCleanOptional } from '@luna/utils';

import { createQueryString } from '../query/createQueryString';
import { useRouteRootConfigContext } from './routeConfigContext';
import { getCurrentUrlWithoutHash } from '../useCurrentRoute';
import type { QueryParameters } from '../types/NavigateOptions';
import { splitPathFromQueryParams } from '../query/QueryParamsService';
import { generatePath } from '../generatePath';

type Parameters = {
    queryParameters: QueryParameters;
    pathParameters: Record<string, string>;
};

type Return = (path: string, { pathParameters, queryParameters }: Parameters) => string;

export const useCreateUrlFromRootMap = (featureId: string): Return => {
    // root config must be injected via context
    const { value: rootMap } = useRouteRootConfigContext();

    return useCallback(
        (path: string, { pathParameters, queryParameters }: Parameters) => {
            if (!rootMap?.[featureId]) {
                // For a certain period of time, route config context can be empty.
                // In this case,let's return a dummy link
                // and relly on context update triggering a re-evaluation
                return '#';
            }

            const rootPrefix = rootMap[featureId as string];

            const currentUrlWithoutHash = getCurrentUrlWithoutHash();
            const paramsFromPath = matchPath(
                { path: rootPrefix, end: false },
                currentUrlWithoutHash,
            );
            const [basePath, baseQueryParams] = splitPathFromQueryParams(path);

            const mergedParams = {
                ...(paramsFromPath?.params ?? {}),
                '*': undefined, // remove * if matched - we want to construct full path on our own,
                ...(pathParameters ?? {}),
            };
            // remove /* suffix
            const pathWithoutExtraSlashes = `/${rootPrefix}/${basePath}`
                .replaceAll('//', '/')
                .replace(/\/$/, '');

            const formattedQueryParameters = createQueryString(
                deepCleanOptional({
                    ...baseQueryParams,
                    ...queryParameters,
                }),
            );
            try {
                return (
                    generatePath(pathWithoutExtraSlashes, mergedParams) + formattedQueryParameters
                );
            } catch (e) {
                const newMessage = `Generating route "${path}" for feature "${featureId}" failed: ${
                    (e as Error)?.message ?? '-'
                }`;
                throw new Error(newMessage);
            }
        },
        [rootMap, featureId],
    );
};
